/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

/*
 * Keep the app dependencies and React context out of `index.tsx` to avoid a
 * circular import: views reach this module through `app-hooks`, while the entry
 * imports the views through the routes. With that cycle, an HMR update can
 * re-run the entry and call `createRoot` on `#root` a second time, eventually
 * failing with `removeChild`.
 */

import { configure } from "mobx"
import type { ReactNode } from "react"
import { createContext, useContext } from "react"

import type { AppComponents } from "./init"
import { syncInit } from "./init"
import { applicationDetailViewModelLookup } from "./viewmodel/application-detail-viewmodel-lookup"

// Require all observable mutations to happen inside actions.
configure({
    enforceActions: "always"
})

/**
 * Initialize the application services and view model container synchronously.
 */
export const appComponents = syncInit()

// Reuse application data from list responses when opening the corresponding detail view.
export const getApplicationDetailVM = applicationDetailViewModelLookup(appComponents.vmContainer)

/**
 * Provide the application dependencies through React context.
 *
 * The context uses the singleton as its default, so `useReactAppContext` is always
 * available and does not need a "used outside a provider" guard.
 */
const ReactAppContext = createContext<AppComponents>(appComponents)

export const ReactAppProvider = ({ children }: { children: ReactNode }) => (
    <ReactAppContext.Provider value={appComponents}>{children}</ReactAppContext.Provider>
)

export const useReactAppContext = (): AppComponents => useContext(ReactAppContext)
